import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import { IInfinitePageParams } from 'services/types';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import { IEditUserBody, IRegisterUserBody, IUserResponse } from './types';

export const useGetUsers = (params: IInfinitePageParams) => {
  const { responseHandler } = useResponseHandler();

  const { getNextPageParam, setTotalPageCount } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<IUserResponse[], DefaultError>({
    queryKey: ['users-infinite-list', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'UserManagement/GetUsers',
        params: { ...params, itemCount, page: pageParam },
      });

      const value = await responseHandler<IUserResponse[]>(response, {
        groupName: `useGetUsers - ${pageParam}`,
      });

      setTotalPageCount(
        !!value.length ? (pageParam as number) + 2 : (pageParam as number)
      );

      return value;
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetUser = (id: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IUserResponse, DefaultError>({
    queryKey: ['userManagement', 'user'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `UserManagement/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useGetUser',
        notFoundPath: '/admin/users',
      });
    },
  });
};

export const useRegisterUser = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useRegisterUser');
  return useMutation<IUserResponse, DefaultError, IRegisterUserBody>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('user.what'),
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: 'UserManagement/Register',
        body,
      });

      return responseHandler(response, {
        groupName: 'useRegisterUser',
        notFoundPath: '/admin/users',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: data.userName ?? i18next.t('user.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useEditUser = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useEditUser');
  return useMutation<IUserResponse, DefaultError, Omit<IEditUserBody, 'id'>>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.edit.loading', {
          what: i18next.t('user.what'),
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: 'UserManagement/User',
        body: {
          ...body,
          id,
        },
      });

      return responseHandler(response, {
        groupName: 'useEditUser',
        notFoundPath: '/admin/users',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: data.userName ?? i18next.t('user.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteUser = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteUser');

  return useMutation<boolean, DefaultError, string>({
    mutationFn: async (userEmail: string) => {
      loading(
        i18next.t('service-code.delete.loading', {
          what: i18next.t('user.what'),
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: 'UserManagement/DeleteUser',
        body: { email: userEmail },
      });

      return responseHandler(response, {
        groupName: `useDeleteUser`,
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.remove.success', {
          what: i18next.t('user.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useActivateUser = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useActivateUser');

  return useMutation<boolean, DefaultError, void>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.active.loading', {
          what: i18next.t('user.what'),
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: 'UserManagement/ActivateUser',
        body: { userId: id },
      });

      return responseHandler(response, {
        groupName: `useActivateUser - ${id}`,
      });
    },

    onSuccess: data =>
      success(
        i18next.t('service-message.active.success', {
          what: i18next.t('user.what'),
        })
      ),
    onError: dismiss,
  });
};

export const useDeactivateUser = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useActivateUser');

  return useMutation<boolean, DefaultError, void>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.deactivate.loading', {
          what: i18next.t('user.what'),
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: 'UserManagement/DeactivateUser',
        body: { userId: id },
      });

      return responseHandler(response, {
        groupName: `useDeactivateUser`,
      });
    },

    onSuccess: data =>
      success(
        i18next.t('service-message.deactivate.success', {
          what: i18next.t('user.what'),
        })
      ),
    onError: dismiss,
  });
};
